import React, { useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { getSafeString } from './campaignAiEngine';

const safeArray = (value) => (Array.isArray(value) ? value : []);

const sectionSx = {
  border: '1px solid rgba(151,113,39,0.18)',
  borderRadius: 1.25,
  p: 0.62,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(248,241,227,0.78) 100%)'
};

const titleSx = {
  color: '#7a4d0b',
  fontSize: '0.9rem',
  fontWeight: 900,
  mb: 0.26
};

const textSx = {
  color: '#2f2516',
  fontSize: '0.82rem',
  lineHeight: 1.26
};

const buildCopyText = (report) => {
  if (!report) return '';
  if (report.loading) return 'Yönetici kampanya raporu hazırlanıyor...';
  return [
    'Yönetici Kampanya Raporu',
    getSafeString(report.title, '-'),
    '',
    `Kısa Özet: ${getSafeString(report.summary, '-')}`,
    `Kampanya: ${getSafeString(report.campaignDetails?.name, '-')}`,
    `Tarih: ${getSafeString(report.campaignDetails?.dateRange, '-')}`,
    `Hedef: ${getSafeString(report.campaignDetails?.goal, '-')}`,
    '',
    `Satış Etkisi: ${getSafeString(report.salesEffectSummary, '-')}`,
    `Kampanya Sonuç Özeti: ${getSafeString(report.resultSummary, '-')}`,
    `Kampanya Beklentisi: ${getSafeString(report.campaignExpectation, '-')}`,
    `AI Yorumu: ${getSafeString(report.aiSalesComment, '-')}`,
    '',
    'Şube Durumu',
    safeArray(report.branchStatusSummary).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Şube Takı Ürün Kıyaslaması',
    safeArray(report.branchSalesSummary).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Takı Ürün / 14 Ayar Özeti',
    safeArray(report.categorySalesSummary).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Mağaza Aksiyonları',
    safeArray(report.storeActions).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Personel Aksiyonları',
    safeArray(report.staffActions).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'İnternet Aksiyonları',
    safeArray(report.internetActions).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Instagram Aksiyonları',
    safeArray(report.instagramActions).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    'Yeni Fikirler',
    safeArray(report.customIdeas).map((item) => `- ${getSafeString(item, '-')}`).join('\n') || '-',
    '',
    `Beklenen Etki: ${getSafeString(report.expectedImpact, '-')}`
  ].join('\n');
};

const ActionList = ({ title, items }) => (
  <Box className="manager-report-section" sx={sectionSx}>
    <Typography sx={titleSx}>{title}</Typography>
    {safeArray(items).slice(0, 2).map((item) => (
      <Typography key={item} sx={textSx}>• {getSafeString(item, '-')}</Typography>
    ))}
  </Box>
);

function ManagerReportModal({ open, report, hasActiveCampaign = true, onClose }) {
  const copyText = useMemo(() => buildCopyText(report), [report]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success('Yönetici raporu kopyalandı', { autoClose: 1600 });
    } catch (e) {
      toast.error('Kopyalama yapılamadı', { autoClose: 1800 });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!hasActiveCampaign) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#fffaf0', color: '#2f2516', border: '1px solid rgba(151,113,39,0.24)', borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#2f2516', fontWeight: 900 }}>Aktif kampanya bulunamadı</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#5f5140' }}>Yönetici raporu oluşturmak için önce aktif bir kampanya seçin.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#7a4d0b' }}>Kapat</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!report) return null;
  if (report.loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#fffaf0', color: '#2f2516', border: '1px solid rgba(151,113,39,0.24)', borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#2f2516', fontWeight: 900 }}>Yönetici Kampanya Raporu</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#5f5140', fontSize: '1rem' }}>Gerçek satış verileri okunuyor, kampanya sonuç raporu hazırlanıyor...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        className: 'manager-report-paper',
        sx: {
          background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
          border: '1px solid rgba(151,113,39,0.24)',
          borderRadius: 2,
          color: '#2f2516',
          width: 'min(94vw, 1180px)',
          height: '88vh',
          maxHeight: '88vh',
          overflow: 'hidden'
        }
      }}
    >
      <style>
        {`
          @media print {
            @page { margin: 14mm; }
            html, body { background: #ffffff !important; }
            body * { visibility: hidden !important; }
            .manager-report-print-area,
            .manager-report-print-area * { visibility: visible !important; }
            .manager-report-paper {
              position: static !important;
              max-height: none !important;
              overflow: visible !important;
              box-shadow: none !important;
              border: 0 !important;
              background: #ffffff !important;
            }
            .manager-report-print-area {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-height: none !important;
              overflow: visible !important;
              background: #ffffff !important;
              color: #111827 !important;
              padding: 0 !important;
              display: block !important;
            }
            .manager-report-actions { display: none !important; }
            .manager-report-section {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              background: #ffffff !important;
              border: 1px solid #d1d5db !important;
              box-shadow: none !important;
            }
            .manager-report-section *,
            .manager-report-print-area * { color: #111827 !important; }
          }
        `}
      </style>

      <DialogTitle sx={{ px: 1.5, pt: 0.85, pb: 0.55, borderBottom: '1px solid rgba(151,113,39,0.14)' }}>
        <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: '1rem' }}>Yönetici Kampanya Raporu</Typography>
        <Typography sx={{ color: '#6b5b40', fontSize: '0.72rem', mt: 0.1 }}>
          {getSafeString(report.title, '-')}
        </Typography>
      </DialogTitle>

      <DialogContent
        className="manager-report-print-area"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 0.48,
          maxHeight: 'calc(88vh - 86px)',
          overflow: 'auto',
          px: 1.3,
          py: 0.5,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'rgba(151,113,39,0.08)' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(151,113,39,0.28)', borderRadius: 99 }
        }}
      >
        <Box className="manager-report-section" sx={{ ...sectionSx, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography sx={titleSx}>Kampanya Özeti</Typography>
          <Typography sx={{ ...textSx, color: '#2f2516', fontWeight: 900 }}>{getSafeString(report.campaignDetails?.name, report.title)}</Typography>
          <Typography sx={textSx}>Tarih: {getSafeString(report.campaignDetails?.dateRange, '-')}</Typography>
          <Typography sx={textSx}>Hedef: {getSafeString(report.campaignDetails?.goal, '-')}</Typography>
          <Typography sx={{ ...textSx, mt: 0.28 }}>{getSafeString(report.summary, '-')}</Typography>
        </Box>

        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Şubeler Ne Alemde?</Typography>
          {safeArray(report.branchStatusSummary).slice(0, 3).map((item) => (
            <Typography key={item} sx={textSx}>• {getSafeString(item, '-')}</Typography>
          ))}
        </Box>
        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Satış Etkisi</Typography>
          <Typography sx={textSx}>{getSafeString(report.salesEffectSummary, '-')}</Typography>
        </Box>

        <Box className="manager-report-section" sx={{ ...sectionSx, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography sx={titleSx}>Takı Ürün Kampanya Sonucu</Typography>
          <Typography sx={textSx}>{getSafeString(report.resultSummary, '-')}</Typography>
        </Box>
        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Kampanya Beklentisi</Typography>
          <Typography sx={textSx}>{getSafeString(report.campaignExpectation, '-')}</Typography>
        </Box>
        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Beklenen Etki</Typography>
          <Typography sx={textSx}>{getSafeString(report.expectedImpact, '-')}</Typography>
        </Box>

        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Takı Ürün / 14 Ayar Özeti</Typography>
          {safeArray(report.categorySalesSummary).slice(0, 3).map((item) => (
            <Typography key={item} sx={textSx}>• {getSafeString(item, '-')}</Typography>
          ))}
        </Box>
        <Box className="manager-report-section" sx={sectionSx}>
          <Typography sx={titleSx}>Şube Takı Ürün Kıyaslaması</Typography>
          {safeArray(report.branchSalesSummary).slice(0, 3).map((item) => (
            <Typography key={item} sx={textSx}>• {getSafeString(item, '-')}</Typography>
          ))}
        </Box>

        <Box className="manager-report-section" sx={{ ...sectionSx, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography sx={titleSx}>AI Yorumu</Typography>
          <Typography sx={textSx}>{getSafeString(report.aiSalesComment, '-')}</Typography>
        </Box>

        <ActionList title="Mağaza Aksiyonları" items={report.storeActions} />
        <ActionList title="Personel Aksiyonları" items={report.staffActions} />
        <ActionList title="Web / Instagram Aksiyonları" items={[...safeArray(report.internetActions), ...safeArray(report.instagramActions)]} />
        {safeArray(report.customIdeas).length ? <ActionList title="Yeni Fikirler" items={report.customIdeas} /> : null}
      </DialogContent>

      <DialogActions
        className="manager-report-actions"
        sx={{
          px: 1.5,
          py: 0.55,
          borderTop: '1px solid rgba(151,113,39,0.14)',
          background: 'rgba(255,250,240,0.96)',
          position: 'sticky',
          bottom: 0
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#4b5563' }}>Kapat</Button>
        <Button onClick={handlePrint} sx={{ textTransform: 'none', color: '#7a4d0b' }}>Yazdır</Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 800, bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
        >
          Kopyala
        </Button>
      </DialogActions>
      <style>{`
        @media (max-width: 600px) {
          .manager-report-paper {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100svh !important;
            max-height: 100svh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .manager-report-actions {
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .manager-report-actions .MuiButton-root {
            flex: 1 1 calc(50% - 4px) !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

export default ManagerReportModal;
